'use client';

import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';

import { TabSwitcher } from '@/components';
import { useT } from '@/hooks';
import {
  useLeaderboardSortCategories,
  useUserClosedPositions,
  useUserPositions,
} from '@/services/query';
import { ProfileActivityTab } from './ProfileActivityTab';
import { ProfilePositionsFilters } from './ProfilePositionsFilters';
import { ProfilePositionsTableContent } from './ProfilePositionsTableContent';

// mainTabOptions will be generated dynamically

const mockPositions = [
  {
    ticker: 'BTC-MAR-2024',
    marketName: 'Will Bitcoin reach $70k by end of March?',
    shares: '500.00',
    avgPrice: 0.45,
    currentPrice: 0.68,
    value: 340.0,
    outcome: 'Yes',
    isProfit: true,
  },
  {
    ticker: 'ETH-MAR-2024',
    marketName: 'Will Ethereum reach $4k by end of March?',
    shares: '1,200.00',
    avgPrice: 0.32,
    currentPrice: 0.28,
    value: 336.0,
    outcome: 'Yes',
    isProfit: false,
  },
  {
    ticker: 'SOL-MAR-2024',
    marketName: 'Will Solana outperfrom ETH in March?',
    shares: '850.00',
    avgPrice: 0.15,
    currentPrice: 0.25,
    value: 212.5,
    outcome: 'No',
    isProfit: true,
  },
];

interface ProfilePositionsTableProps {
  address: string;
}

export const ProfilePositionsTable = ({ address }: ProfilePositionsTableProps) => {
  const [mainTab, setMainTab] = useState('Positions');
  const [tab, setTab] = useState('Active');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const { t } = useT();

  const mainTabOptions = [
    { label: t('leaderboardPortfolio.positions'), value: 'Positions' },
    { label: t('leaderboardPortfolio.activity'), value: 'Activity' },
  ];

  const handleTabChange = (newTab: string) => {
    setTab(newTab);
    setSortBy('');
  };

  // Fetch sort categories based on active/closed tab
  const categoryType = tab === 'Active' ? 'active-positions' : 'closed-positions';
  const { data: sortCategories } = useLeaderboardSortCategories(categoryType);

  // Derive the active sort key (fallback to default if current state is invalid for the new category)
  const effectiveSortBy = useMemo(() => {
    if (!sortCategories || sortCategories.length === 0) return sortBy || 'CURRENT';
    if (sortBy && sortCategories.some((c) => c.key === sortBy)) return sortBy;

    const defaultOption =
      sortCategories.find((c) => c.key === 'CURRENT' || c.key === 'VALUE') || sortCategories[0];
    return defaultOption?.key || 'CURRENT';
  }, [sortCategories, sortBy]);

  // Sync state only if needed
  useEffect(() => {
    if (effectiveSortBy !== sortBy) {
      const timeout = setTimeout(() => {
        setSortBy(effectiveSortBy);
      }, 0);
      return () => clearTimeout(timeout);
    }
  }, [effectiveSortBy, sortBy]);

  const isActiveTab = tab === 'Active';

  const sortByParam = (sortBy || effectiveSortBy).toUpperCase();

  const {
    data: activePositionsData,
    isLoading: isActiveLoading,
    fetchNextPage: fetchNextActivePage,
    hasNextPage: hasNextActivePage,
    isFetchingNextPage: isFetchingNextActivePage,
  } = useUserPositions(
    {
      user: address,
      title: search || undefined,
      sizeThreshold: 1,
      sortBy: sortByParam,
      sortDirection: 'DESC',
    },
    { enabled: !!address && isActiveTab },
  );

  const {
    data: closedPositionsData,
    isLoading: isClosedLoading,
    fetchNextPage: fetchNextClosedPage,
    hasNextPage: hasNextClosedPage,
    isFetchingNextPage: isFetchingNextClosedPage,
  } = useUserClosedPositions(
    {
      user: address,
      title: search || undefined,
      sortBy: sortByParam,
      sortDirection: 'DESC',
    },
    { enabled: !!address && !isActiveTab },
  );

  const positions = isActiveTab
    ? activePositionsData?.pages.flatMap((page) => page) || []
    : closedPositionsData?.pages.flatMap((page) => page) || [];

  const isDataLoading = isActiveTab ? isActiveLoading : isClosedLoading;
  const fetchNextPage = isActiveTab ? fetchNextActivePage : fetchNextClosedPage;
  const hasNextPage = isActiveTab ? hasNextActivePage : hasNextClosedPage;
  const isFetchingNextPage = isActiveTab ? isFetchingNextActivePage : isFetchingNextClosedPage;

  return (
    <Box>
      {/* Top Level Navigation */}
      <TabSwitcher
        options={mainTabOptions}
        activeTab={mainTab}
        onTabChange={setMainTab}
        variant="pills"
        containerSx={{ mb: 2 }}
      />

      {mainTab === 'Positions' ? (
        <Box>
          <ProfilePositionsFilters
            tab={tab}
            onTabChange={handleTabChange}
            search={search}
            onSearchChange={setSearch}
            sortBy={sortBy}
            onSortChange={setSortBy}
            effectiveSortBy={effectiveSortBy}
          />

          <ProfilePositionsTableContent
            positions={positions}
            tab={tab}
            isLoading={isDataLoading}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        </Box>
      ) : (
        <ProfileActivityTab address={address} />
      )}
    </Box>
  );
};
